// Modal — port of legacy `src/ui/Modal.jsx` with Phase-4 a11y additions
// (focus-trap + focus-return.md). Portals into `#modal-host`
// rendered by `shell/ModalHost` (falls back to `document.body` in
// tests/SSR where the host isn't present).
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon.js';
import { useNarrow } from './useNarrow.js';

const FOCUSABLE_SELECTOR =
  'input, select, textarea, [href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** 44 px minimum touch target for the opted-in sheet's close icon. */
const NARROW_TAP_TARGET = 'calc(var(--control-h) + var(--s-4))';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  width?: number | string;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * When true, the modal swallows close attempts via the title close icon,
   * Escape key, and backdrop click. Visible action buttons inside the modal
   * remain the only dismissal path. Used by busy-guarded confirm flows where
   * losing the modal to a stray Escape would race with an in-flight save or
   * upload.
   */
  dismissDisabled?: boolean;
  /**
   * When provided and points to a focusable element on open, that element
   * receives initial keyboard focus instead of the first focusable inside
   * the sheet. Lets confirm dialogs put focus on the safe primary action
   * rather than the title close icon.
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Opt-in mobile presentation. The sheet tracks the visual viewport at every
   * width, so the header and footer stay put while the body scrolls — including
   * with a software keyboard open — and it keeps the device safe areas and a
   * 44 px close target. Below 900 px it additionally drops its `width`-derived
   * dimensions and fills the viewport. Off by default: every existing consumer
   * keeps its current dimensions and chrome at every viewport.
   */
  narrowFullWidth?: boolean;
}

interface SheetViewport {
  height: number;
  offsetTop: number;
}

/**
 * Track `window.visualViewport` while `enabled`.
 *
 * WHY: a software keyboard resizes the VISUAL viewport and leaves the layout
 * viewport — and therefore `100dvh` — untouched, which parks the footer
 * actions behind the keyboard.
 *
 * INVARIANT: returns `null` (caller falls back to `100dvh`) when the browser
 * exposes no visual viewport and while the user pinch-zooms — during zoom the
 * visual viewport is a pan window, so sizing the sheet to it would fight the
 * user's own zoom/scroll.
 */
function useVisualViewportSheet(enabled: boolean): SheetViewport | null {
  const [metrics, setMetrics] = useState<SheetViewport | null>(null);
  useEffect(() => {
    if (!enabled) {
      setMetrics(null);
      return;
    }
    const vv = typeof window === 'undefined' ? null : window.visualViewport;
    if (!vv) return;
    const read = () => {
      setMetrics(vv.scale > 1.01 ? null : { height: vv.height, offsetTop: vv.offsetTop });
    };
    read();
    vv.addEventListener('resize', read);
    vv.addEventListener('scroll', read);
    return () => {
      vv.removeEventListener('resize', read);
      vv.removeEventListener('scroll', read);
    };
  }, [enabled]);
  return enabled ? metrics : null;
}

export function Modal({
  open,
  onClose,
  title,
  width = 480,
  children,
  footer,
  dismissDisabled = false,
  initialFocusRef,
  narrowFullWidth = false,
}: ModalProps) {
  const { t } = useTranslation('common');
  const narrow = useNarrow();
  const fullWidth = narrowFullWidth && narrow;
  // A closed modal renders nothing, so it must not hold viewport listeners.
  // WHY: keyed on the opt-in alone, not on `fullWidth` — a phone held in
  // landscape is over 900 px wide yet has the shortest visible area of all
  // once the keyboard opens.
  const sheetViewport = useVisualViewportSheet(open && narrowFullWidth);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<Element | null>(null);
  const onCloseRef = useRef(onClose);
  const dismissDisabledRef = useRef(dismissDisabled);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    dismissDisabledRef.current = dismissDisabled;
  }, [dismissDisabled]);

  function attemptDismiss(): void {
    if (dismissDisabledRef.current) return;
    onCloseRef.current?.();
  }

  const [mounted, setMounted] = useState(false);

  // Animate-in on open; reset on close (legacy behavior).
  useEffect(() => {
    if (!open) {
      setMounted(false);
      return;
    }
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Capture trigger + Escape + initial focus (legacy behavior + Phase-4 trigger capture).
  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement instanceof Element ? document.activeElement : null;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') attemptDismiss();
    };
    document.addEventListener('keydown', onKey);
    const raf = requestAnimationFrame(() => {
      const explicit = initialFocusRef?.current ?? null;
      if (explicit && explicit.isConnected) {
        explicit.focus();
        return;
      }
      const first =
        boxRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
        boxRef.current?.querySelector<HTMLElement>('button') ??
        null;
      if (first) first.focus();
    });
    return () => {
      document.removeEventListener('keydown', onKey);
      cancelAnimationFrame(raf);
    };
  }, [open]);

  // Focus-return when open flips back to false (or component unmounts while open).
  useEffect(() => {
    if (open) return;
    const t = triggerRef.current;
    if (t && t instanceof HTMLElement && t.isConnected) t.focus();
  }, [open]);
  useEffect(() => {
    return () => {
      const t = triggerRef.current;
      if (t && t instanceof HTMLElement && t.isConnected) t.focus();
    };
  }, []);

  // Tab-trap inside the sheet while open.
  useEffect(() => {
    if (!open) return;
    const box = boxRef.current;
    if (!box) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusables = Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled'),
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    box.addEventListener('keydown', onKey);
    return () => box.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const sheetWidth = `min(${typeof width === 'number' ? width + 'px' : width}, calc(100vw - 48px))`;

  // Opted-in sheets pin themselves to the visible area instead of the layout
  // viewport at every width. `position: fixed` resolves against the layout
  // viewport, so `top: offsetTop` plus the visual-viewport height put the sheet
  // over what the user actually sees. Above 900 px the width stays declared.
  const viewportSheet: CSSProperties | null = narrowFullWidth
    ? {
        position: 'fixed',
        right: 0,
        top: sheetViewport ? sheetViewport.offsetTop : 0,
        height: sheetViewport ? `${sheetViewport.height}px` : '100dvh',
      }
    : null;

  // Below 900 px the sheet also goes full-bleed: the edge treatments of the
  // right-hand sheet have nothing to sit against (mirrors `Drawer`'s narrow
  // mode).
  const fullWidthSheet: CSSProperties | null = fullWidth
    ? {
        left: 0,
        width: 'auto',
        borderLeft: 'none',
        boxShadow: 'none',
      }
    : null;

  // An opted-in sheet reaches the physical screen edges, so every row of
  // content and every action keeps the device's own side insets on top of its
  // 16 px gutter. Default consumers never touch those edges.
  const safeAreaX: CSSProperties = narrowFullWidth
    ? {
        paddingLeft: 'calc(var(--s-5) + env(safe-area-inset-left))',
        paddingRight: 'calc(var(--s-5) + env(safe-area-inset-right))',
      }
    : {};

  const portalTarget =
    typeof document !== 'undefined'
      ? (document.getElementById('modal-host') ?? document.body)
      : null;

  const node = (
    // Legacy-faithful backdrop: plain <div> whose click dismisses.
    // Escape-to-close is bound at `document` keydown above, so keyboard
    // users are covered; this div is purely a pointer-dismiss affordance.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={attemptDismiss}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: mounted ? 'var(--scrim)' : 'transparent',
        transition: 'background var(--dur-2) var(--ease-out)',
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      {/* Sheet stops click propagation so inner content is not dismissed on accident. */}
      {/* role=dialog is an interactive role for a11y tools, but the click
          handler here only stops propagation — no user action. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={boxRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: sheetWidth,
          maxWidth: '100%',
          height: '100vh',
          background: 'var(--bg-1)',
          color: 'var(--fg-1)',
          borderLeft: '1px solid var(--border-1)',
          boxShadow: 'var(--shadow-3)',
          transform: mounted ? 'translateX(0)' : 'translateX(24px)',
          opacity: mounted ? 1 : 0,
          transition:
            'transform var(--dur-2) var(--ease-out), opacity var(--dur-2) var(--ease-out)',
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          outline: 'none',
          ...(viewportSheet ?? {}),
          ...(fullWidthSheet ?? {}),
        }}
      >
        {title && (
          <div
            style={{
              padding: '14px 16px',
              ...safeAreaX,
              // Keeps the header's own 14 px (--s-1 + --s-4) above the notch.
              ...(narrowFullWidth
                ? { paddingTop: 'calc(var(--s-1) + var(--s-4) + env(safe-area-inset-top))' }
                : {}),
              borderBottom: '1px solid var(--border-2)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flex: 'none',
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 600, flex: 1 }}>{title}</div>
            <button
              onClick={attemptDismiss}
              aria-label={t('actions.close')}
              disabled={dismissDisabled}
              style={{
                width: 28,
                height: 28,
                // A thumb needs 44 px; the icon itself is unchanged.
                ...(narrowFullWidth
                  ? { width: NARROW_TAP_TARGET, height: NARROW_TAP_TARGET, flex: 'none' }
                  : {}),
                borderRadius: 'var(--r-1)',
                background: 'transparent',
                border: 'none',
                cursor: dismissDisabled ? 'not-allowed' : 'pointer',
                opacity: dismissDisabled ? 0.45 : 1,
                color: 'var(--fg-3)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="x" size={16} />
            </button>
          </div>
        )}
        <div style={{ padding: '16px', ...safeAreaX, overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {children}
        </div>
        {footer && (
          <div
            style={{
              padding: '12px 16px',
              ...safeAreaX,
              // Keeps the actions clear of the home indicator.
              ...(narrowFullWidth
                ? { paddingBottom: 'calc(var(--s-4) + env(safe-area-inset-bottom))' }
                : {}),
              borderTop: '1px solid var(--border-2)',
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              flex: 'none',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return portalTarget ? createPortal(node, portalTarget) : node;
}
